import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from 'tailwind-v4/lib/utils'
import { GLASS } from '../occupancy'
import { PANEL_LABEL, type PanelId } from './types'

/**
 * The chrome every panel shares: a title, a close, a scroll area.
 *
 * Shared so three panels cannot drift into three different headers, and
 * so the scroll lives in one place: a panel that scrolls its whole self
 * including its title is a panel whose title you lose.
 */
export function PanelFrame({
  id,
  onClose,
  children,
  note,
}: {
  id: PanelId
  onClose: () => void
  children: ReactNode
  /** A quieter line under the title: what this panel cannot see. */
  note?: string
}) {
  return (
    <section
      aria-label={PANEL_LABEL[id]}
      className={cn(
        GLASS,
        'pointer-events-auto flex min-h-0 min-w-0 flex-1 flex-col',
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <h2 className="truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {PANEL_LABEL[id]}
          </h2>
          {note ? (
            <p className="truncate text-[11px] text-muted-foreground/70">{note}</p>
          ) : null}
        </div>
        <button
          type="button"
          aria-label={`Close ${PANEL_LABEL[id]}`}
          onClick={onClose}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="size-3.5" />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">{children}</div>
    </section>
  )
}

/** A label/value row, the shape most of these panels are made of. */
export function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 py-1.5">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
        {label}
      </span>
      <span className="break-words text-sm text-foreground">{value}</span>
    </div>
  )
}
